import { BasePoint, Editor, Element, Location, Range, Text, Transforms } from 'slate'

import { DriftUserIF } from '@driftt/app-config/dist/utils/driftTypes'

import { getDisplayName } from './user'

export const insertMention = (editor: Editor, label: string, userId: number) => {
  const mention = [
    {
      type: 'mention',
      mentionLabel: label,
      mentionUserId: userId,
      children: [{ text: label }]
    },
    { text: ' ' }
  ]
  Transforms.insertNodes(editor, mention)
  Transforms.move(editor, { distance: 2 })
}

const MAX_MENTION_OPTIONS_TO_RENDER = 20

export const getMentionOptions = (
  orgUsersById: Record<number, DriftUserIF | undefined>,
  search: string | null
) => {
  return Object.entries(orgUsersById)
    .filter(([, user]) => user?.status === 'ENABLED' && !user.bot && (user.name || user.email))
    .map(([, user]) => ({ user, label: getDisplayName(user) }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .filter(({ label }) => !search || label.toLowerCase().startsWith(search.toLowerCase()))
    .slice(0, MAX_MENTION_OPTIONS_TO_RENDER)
}

const getCurrentMentionRange = (editor: Editor, start: BasePoint) => {
  const currentNode = Editor.node(editor, start)[0]
  if (!Text.isText(currentNode)) {
    return
  }

  const textCandidate = currentNode.text.substring(0, start.offset)
  const mentionMatch = textCandidate.match(/@\w*((\s\w*)|(@\w*\.?\w*))?$/)
  if (mentionMatch?.index !== undefined) {
    return Editor.range(editor, { path: start.path, offset: mentionMatch.index }, start)
  }
}

const isWhitespaceNext = (editor: Editor, start: Location) => {
  const after = Editor.after(editor, start)
  const afterRange = Editor.range(editor, start, after)
  const afterText = Editor.string(editor, afterRange)
  return afterText.match(/^(\s|$)/)
}

const isWhitespaceBefore = (editor: Editor, start: Location) => {
  const before = Editor.before(editor, start)
  const beforeRange = before && Editor.range(editor, before, start)
  const beforeText = beforeRange && Editor.string(editor, beforeRange)
  return !beforeText || beforeText.match(/^\s?$/)
}

export const parseMentionTarget = (editor: Editor) => {
  const { selection } = editor

  if (selection && Range.isCollapsed(selection)) {
    const [start] = Range.edges(selection)

    const mentionRange = getCurrentMentionRange(editor, start)

    if (
      mentionRange &&
      isWhitespaceBefore(editor, mentionRange.anchor) &&
      isWhitespaceNext(editor, start)
    ) {
      const mentionText = Editor.string(editor, mentionRange)
      if (mentionText) {
        return {
          mentionTargetRange: mentionRange,
          mentionSearchText: mentionText.substring(1)
        }
      }
    }
  }

  return null
}

export const parseMentionedUsers = (nodes: any[]) => {
  const mentionedUsers = [] as string[]
  nodes.forEach(node =>
    node.children?.forEach(
      (child: Element) => child.mentionUserId && mentionedUsers.push(`USER:${child.mentionUserId}`)
    )
  )
  return mentionedUsers
}
